<template>
  <v-app>
    <v-container fluid class="pa-0">
      <v-overlay v-if="displayGlobalOverlay" z-index="8" opacity="0.9">
        <v-container>
          <p class="text-center">{{ overlayMessage }}</p>
          <v-row justify="center"><v-progress-circular indeterminate color="primary"></v-progress-circular></v-row>
        </v-container>
      </v-overlay>
      <app-bar v-if="displayNavigation" />
      <navigation-drawer v-if="displayNavigation" />

      <!-- Digital in store iframe -->
      <div id="digital-in-store" v-if="openDigitalInStorePanel">
        <v-btn class="close" color="primary" fab small @click="openDigitalInStorePanel = false">
          <v-icon>mdi-close-circle</v-icon>
        </v-btn>
        <iframe ref="digitalInStoreIframe" title="Digital in Store" :src="digitalInStoreUrl"></iframe>
      </div>

      <main-view :key="keyToReloadComponent" />
      <v-snackbar v-model="snackbar.display" :color="snackbar.color" :timeout="6000" bottom multi-line right>
        {{ snackbar.text }}
      </v-snackbar>
      <refresh-dialog v-model="refreshDialog" />
      <selection-location-dialog
        v-model="selectionLocationDialog"
        :currentStore="currentStoreDetails"
        :is-admin="isAdmin"
        :is-shop-manager="isShopManager"
        :user-locations="userStores"
        @close="selectionLocationDialog = false"
      />
    </v-container>
  </v-app>
</template>

<script>
  import { mapActions, mapState } from 'vuex'
  import AppBar from '../components/AppBar'
  import MainView from '../components/MainView'
  import NavigationDrawer from '../components/NavigationDrawer'
  import RefreshDialog from '../components/dialogs/RefreshDialog'
  import selectionLocationDialog from '../components/dialogs/selectionLocationDialog.vue'
  import { EventBus } from '../event-bus'
  import { LocationService } from '../services/locationService'

  export default {
    name: 'App',
    data() {
      return {
        snackbar: {
          display: false,
          text: '',
          color: '',
        },
        refreshDialog: false,
        selectionLocationDialog: false,
        keyToReloadComponent: Date.now(),
        openDigitalInStorePanel: false,
        digitalInStoreUrl: null,
        displayGlobalOverlay: false,
        overlayMessage: '',
      }
    },
    components: {
      'app-bar': AppBar,
      'navigation-drawer': NavigationDrawer,
      'main-view': MainView,
      RefreshDialog,
      selectionLocationDialog,
    },
    computed: {
      ...mapState([
        'currentStore',
        'userStores',
        'currentStoreDetails',
        'isAdmin',
        'isShopManager',
        'displayNavigation',
      ]),
    },
    async mounted() {
      // Register EventBus listeners first to ensure they are available
      EventBus.$on('displayGlobalOverlay', (args) => {
        this.displayGlobalOverlay = args.display
        this.overlayMessage = args.message
      })
      // Vuetify starts the timeout when the snackbar opens, not when its text changes: a message
      // replacing a visible one inherited whatever was left of the previous delay, and could show for
      // a second. Closing first, then reopening on the next tick, gives every message its full time.
      EventBus.$on('displaySnackbar', (payload) => {
        this.snackbar.display = false
        this.$nextTick(() => {
          this.snackbar = { ...payload }
        })
      })
      EventBus.$on('refreshModal', () => {
        this.refreshDialog = true
      })
      EventBus.$on('selectLocationDialog', () => {
        this.selectionLocationDialog = true
      })
      EventBus.$on('reloadComponent', () => {
        this.keyToReloadComponent = Date.now()
      })
      EventBus.$on('openDigitalInStore', (path) => {
        this.openDigitalInStore(path)
      })

      if (this.$route.query.locale) {
        this.setLocale(this.$route.query.locale.toLowerCase())
      }

      // Check integration_mode query param and update state
      const displayNavigation = this.$route.query.integration_mode !== 'true'
      await this.$store.dispatch('setDisplayNavigation', displayNavigation)

      // Track if store was changed via URL param
      let storeChangedFromUrl = false

      if (this.$route.query.current_store) {
        const requestedStoreId = this.$route.query.current_store

        // Check if requested store is in other_locations
        const isAuthorized = this.userStores.includes(requestedStoreId)

        if (!isAuthorized) {
          // Use store name if available or store id
          let storeName = requestedStoreId

          // Try to get store details to display a better error message
          try {
            const storeDetails = await LocationService.getLocation(
              this.$auth.accessToken,
              requestedStoreId,
              { fields: ['location_id', 'name'] }
            )
            if (storeDetails?.name) {
              storeName = storeDetails.name
            }
          } catch (e) {
            // Failed to fetch store details, fallback to ID
            console.error('Unable to fetch store details for unauthorized store:', e)
          }

          // Redirect to error page
          await this.$router.replace({
            name: 'errors',
            params: {
              error: 'unauthorizedStore',
              displayLogoutBtn: false,
              storeName: storeName,
            },
          })
          return
        }

        // Define requested store as current store if authorized
        if (requestedStoreId !== this.currentStore) {
          await this.$store.dispatch('setCurrentStore', requestedStoreId)
          storeChangedFromUrl = true
        }
      }

      if (this.currentStore) {
        try {
          const res = await LocationService.getLocation(this.$auth.accessToken, this.currentStore)
          await this.setCurrentStoreDetails(res)
        } catch (e) {
          console.error('Unable to fetch current store details:', e)
        }
      }

      // Reload component if store was changed from URL param (same logic as selectionLocationDialog)
      if (storeChangedFromUrl) {
        EventBus.$emit('reloadComponent')
        EventBus.$emit('reLoadCounter')
      }
    },
    methods: {
      ...mapActions(['setCurrentStoreDetails', 'setLocale']),
      openDigitalInStore(path) {
        const settings = this.$store.state.partnerSettings.digital_in_store_access
        if (settings?.enabled) {
          this.digitalInStoreUrl = `${settings.url}${path}`

          if (settings.open_in_panel) {
            // Open in panel
            window.addEventListener('message', (event) => {
              if (event.data === 'getDisplayState') {
                this.$refs.digitalInStoreIframe.contentWindow.postMessage('inOmsPanel', '*')
              } else if (event.data.action === 'redirect_to_order') {
                // Redirect to order
                this.$router.push('/orders/' + event.data.order_id)
              }
            })

            this.openDigitalInStorePanel = true
          } else {
            // Open in new tab
            window.open(this.digitalInStoreUrl, '_blank').focus()
          }
        }
      },
    },
  }
</script>

<style scoped>
  #digital-in-store {
    position: fixed;
    right: 0;
    top: 0;
    width: 33%;
    height: 100vh;
    background: white;
    z-index: 99999;
  }

  #digital-in-store iframe {
    height: 100%;
    width: 100%;
  }

  #digital-in-store .close {
    position: absolute;
    left: -20px;
  }

  @media (max-width: 960px) {
    #digital-in-store {
      width: 50%;
    }
  }

  @media (max-width: 600px) {
    #digital-in-store {
      width: 90%;
    }
  }
</style>
